import i18n from '@/i18n';
import { MINUTES_IN_HOUR } from '@/lib/date-time/constants';
import { Icon, IconMap, IconType } from '@/lib/enum/Icon';
import { belongsToEmployee } from '@/lib/filters/employeeFilters';
import { ScheduleV2FilterType } from '@/lib/schedule-v2/ScheduleV2Filter';
import { ScheduleViewOptionsType } from '@/lib/schedule-v2/ScheduleViewOptions';
import { isAssigned, isLocked } from '@/lib/schedule-v2/shiftV2Status';
import { sortBy } from '@/util/arrayFunctions';
import { isFuture, isPresent } from '@/util/dateArithmetic';
import { getEntityName, minutesRemaining } from '@/util/entityFunctions';
import { keyOfFirstTruthyValue } from '@/util/objectFunctions';
import {
  Employee,
  JobRole,
  Shift,
  ShiftConflictTypeEnum,
  ShiftSwap,
  ShiftSwapStatusEnum,
  Tag,
  TimesheetEntry,
  TimesheetEntryStatusEnum,
} from '../../../api/v1';
import { fullName } from '../employee/employeeFunctions';
import { userCan } from '../permission/userCan';

export enum MinutesPresentationFormat {
  Narrow = 'Narrow',
  Short = 'Short',
}
export const presentTotalDuration = (
  totalMinutes: number,
  format: MinutesPresentationFormat = MinutesPresentationFormat.Narrow,
): string => {
  // Settled on the nearest minute first: decimal hours added together can fall a hair
  // short of a whole minute (5.8 + 10 x 11.6 comes to 121.79999999999998 hours) and
  // would otherwise show a minute less
  const wholeMinutes = Math.round(totalMinutes);
  const hours = Math.floor(wholeMinutes / MINUTES_IN_HOUR);
  const minutes = wholeMinutes - hours * MINUTES_IN_HOUR;
  if (format === MinutesPresentationFormat.Narrow) {
    return `${hours}h ${minutes}m`;
  }
  if (format === MinutesPresentationFormat.Short) {
    return `${i18n.tc('unitWithAmount.hr', hours)} ${
      minutes ? i18n.tc('unitWithAmount.min', minutes) : ''
    }`.trim();
  }
};

export const totalDurationOfShiftsInMinutes = (shifts: Shift[]): number =>
  shifts.reduce(
    (total, shift) =>
      total + shift.expectedWorkingTimeInMinutes + shift.paidBreakInMinutes,
    0,
  );

export const totalDurationOfShiftsInHours = (shifts: Shift[]): number => {
  return totalDurationOfShiftsInMinutes(shifts) / 60;
};

// Used for assign dialogs to check if we need to create a replacement or just reassign
export const isReplacementShift = (shift: Shift, timezone: string) =>
  isLocked(shift) ||
  (isPresent(shift.startsAt, shift.endsAt) &&
    minutesRemaining(timezone, shift) > 15);

export const getShiftTagNames = ({ tagIds }: Shift, tags: Tag[]) =>
  tags
    .filter((t) => tagIds.includes(t.id))
    .map(({ id }) => getEntityName(id, tags));

export const isPendingShiftSwap = (s: ShiftSwap) =>
  [
    ShiftSwapStatusEnum.PendingAcceptance,
    ShiftSwapStatusEnum.PendingApproval,
  ].includes(s.status);

export const shiftSwapPendingApproval = (ss: ShiftSwap): boolean =>
  ShiftSwapStatusEnum.PendingApproval === ss.status;

export const getEmployeesNextShift = (
  employeeId: number,
  shifts: Shift[],
  timesheets: TimesheetEntry[],
  now: Date,
): Shift | null => {
  const filteredShifts = sortBy(
    shifts.filter((s) => belongsToEmployee(employeeId)(s)),
    'startsAt',
  );
  return (
    filteredShifts.find((s) => {
      const timesheet = timesheets.find((t) => t.shiftId === s.id);
      return (
        // Shift with an in-progress timesheet
        (timesheet &&
          timesheet.status !== TimesheetEntryStatusEnum.Completed) ||
        // Shift which ends in future and doesn't have a timesheet
        (s.endsAt > now && !timesheet)
      );
    }) ?? null
  );
};

export const tagVisibilityFilter =
  (userCanManageShifts: boolean) =>
  (tag: Tag): boolean => {
    return userCanManageShifts || tag.visibleToEmployees;
  };

export const totalShiftBreakMinutes = (shift: Shift): number =>
  shift.unpaidBreakInMinutes + shift.paidBreakInMinutes;

export const totalShiftBreakDuration = (shift: Shift): number =>
  shift.scheduledBreaks.reduce(
    (total, { durationInMinutes }) => total + durationInMinutes,
    0,
  );

export const shiftJobRoleIsVisible = (showJobRole: boolean): boolean =>
  showJobRole || userCan.manageShifts();

export const shiftBorderColour = (
  shift: Shift,
  jobRole: JobRole,
  showPublishedStatus: boolean = true,
): string => {
  if (!jobRole) {
    return 'white';
  }
  const { colour } = jobRole;
  const { showJobRole, publishedAt } = shift;

  if (shiftJobRoleIsVisible(showJobRole)) {
    return publishedAt || !showPublishedStatus
      ? colour
      : `repeating-linear-gradient(-45deg, ${colour}, ${colour} 4px, ${colour}99 4px, ${colour}99 8px)`;
  }
  return 'white';
};

export const shiftHasConflictType = (
  shift: Shift,
  conflictType: ShiftConflictTypeEnum,
) => shift.shiftConflicts.some((conflict) => conflict.type === conflictType);

export const shiftHasConflict = (shift: Shift): boolean => {
  if (
    shift.shiftConflicts.length === 1 &&
    shiftHasConflictType(shift, ShiftConflictTypeEnum.Absence)
  ) {
    return isFuture(shift.endsAt);
  }
  return !!shift.shiftConflicts.length;
};

export const conflictWarningMessage = (
  shift: Shift,
  employee: Employee | null,
) => {
  if (
    !userCan.manageShifts() ||
    !isAssigned(shift) ||
    !shiftHasConflict(shift)
  ) {
    return '';
  }
  if (shiftHasConflictType(shift, ShiftConflictTypeEnum.Shift)) {
    return i18n.tc('tooltip.employeeShiftAtThisTime', 0, {
      employee: fullName(employee),
    });
  }
  if (shiftHasConflictType(shift, ShiftConflictTypeEnum.Unavailability)) {
    return i18n.tc('tooltip.employeeUnavailabilityAtThisTime', 0, {
      employee: fullName(employee),
    });
  }
  if (
    shiftHasConflictType(shift, ShiftConflictTypeEnum.Absence) &&
    isFuture(shift.endsAt)
  ) {
    return i18n.tc('tooltip.employeeAbsenceAtThisTime', 0, {
      employee: fullName(employee),
    });
  }
  if (shiftHasConflictType(shift, ShiftConflictTypeEnum.Leave)) {
    return i18n.tc('tooltip.employeeLeaveAtThisTime', 0, {
      employee: fullName(employee),
    });
  }
  return '';
};

export const conflictWarningIcon = (shift: Shift): IconType => {
  return keyOfFirstTruthyValue({
    [IconMap.Absence]: shiftHasConflictType(
      shift,
      ShiftConflictTypeEnum.Absence,
    ),
    [Icon.Plane]: shiftHasConflictType(shift, ShiftConflictTypeEnum.Leave),
    [Icon.CalendarXmark]: shiftHasConflictType(
      shift,
      ShiftConflictTypeEnum.Unavailability,
    ),
    [Icon.CalendarDay]: shiftHasConflictType(
      shift,
      ShiftConflictTypeEnum.Shift,
    ),
  });
};

export const displayShiftOnSchedule =
  (viewOptions: ScheduleViewOptionsType, filter: ScheduleV2FilterType) =>
  (shift: Shift): boolean =>
    viewOptions.showShiftsFromOtherSchedules ||
    shift.scheduleId === filter.scheduleId;
